import { useState } from "react";
import {
  useRecordContext,
  useNotify,
  useRefresh,
  ArrayField,
  Datagrid,
  EmailField,
  FunctionField,
  useDataProvider,
} from "react-admin";
import {
  Box,
  Button,
  TextField as MuiTextField,
  Collapse,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VerifiedUser as VerifyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { AlertType, Client, ClientContact, ResourceName } from "@/types";
import { ConfirmLabelType, ColorType } from "@/types/shared/uiComponents";
import { PhoneField } from "@/components/common/fields/PhoneField";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { isValidEmail, isValidPhone } from "@/utils/helpers/validationHelpers";
import { formatPhoneNumber } from "@/utils/validators/inPlaceValidators";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

const emptyContact: ClientContact = {
  contact_name: "",
  email: "",
  phone: "",
  job_title: "",
};

const emptyErrors = {
  email: "",
  phone: "",
};

const validateContact = (contact: ClientContact) => ({
  email: isValidEmail(contact.email),
  phone: isValidPhone(contact.phone),
});

const hasErrors = (errors: typeof emptyErrors) => Object.values(errors).some((e) => e !== "");

export const ClientContactsList = () => {
  const record = useRecordContext<Client>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();

  // Check edit permissions for clients (contacts belong to clients)
  const { canEdit } = useResourcePermissions(ResourceName.CLIENTS);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState(emptyContact);
  const [newContactErrors, setNewContactErrors] = useState(emptyErrors);
  const [isLoading, setIsLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editData, setEditData] = useState(emptyContact);
  const [editErrors, setEditErrors] = useState(emptyErrors);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const contacts = record?.contacts || [];
  const clientId = record?.id;

  const getContactIndex = (contact: ClientContact): number => {
    return contacts.findIndex(
      (c: ClientContact) => c.contact_name === contact.contact_name && c.email === contact.email
    );
  };

  const handleAddContact = async () => {
    if (!clientId || !newContact.contact_name) return;

    const errors = validateContact(newContact);
    setNewContactErrors(errors);
    if (hasErrors(errors)) return;

    setIsLoading(true);
    try {
      await dataProvider.create(`clients/${clientId}/contacts`, {
        data: { ...newContact, phone: formatPhoneNumber(newContact.phone) },
      });
      notify("Contact added", { type: AlertType.SUCCESS });
      setNewContact(emptyContact);
      setNewContactErrors(emptyErrors);
      setShowAddForm(false);
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateContact = async (index: number) => {
    if (!clientId) return;

    const errors = validateContact(editData);
    setEditErrors(errors);
    if (hasErrors(errors)) return;

    setIsLoading(true);
    try {
      await dataProvider.update(`clients/${clientId}/contacts`, {
        id: index,
        data: { ...editData, phone: formatPhoneNumber(editData.phone) },
        previousData: contacts[index],
      });
      notify("Contact updated", { type: AlertType.SUCCESS });
      setEditingIndex(null);
      setEditErrors(emptyErrors);
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteDialog = (index: number) => {
    setDeleteIndex(index);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleDeleteContact = async () => {
    if (!clientId || deleteIndex === null) return;
    setIsLoading(true);
    try {
      await dataProvider.delete(`clients/${clientId}/contacts`, {
        id: deleteIndex,
      });
      notify("Contact deleted", { type: AlertType.SUCCESS });
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsLoading(false);
      closeDeleteDialog();
    }
  };

  const handleVerifyContact = async (index: number) => {
    if (!clientId) return;
    setIsLoading(true);
    try {
      await dataProvider.invoke(`clients/${clientId}/contacts/${index}/verify`);
      notify("Contact verified", { type: AlertType.SUCCESS });
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPrimary = async (index: number) => {
    if (!clientId) return;
    setIsLoading(true);
    try {
      await dataProvider.invoke(`clients/${clientId}/contacts/${index}/primary`);
      notify("Primary contact updated", { type: AlertType.SUCCESS });
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (contact: ClientContact, index: number) => {
    setEditingIndex(index);
    setEditData({
      contact_name: contact.contact_name,
      email: contact.email,
      phone: contact.phone || "",
      job_title: contact.job_title || "",
    });
    setEditErrors(emptyErrors);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditData(emptyContact);
    setEditErrors(emptyErrors);
  };

  const handleCancel = () => {
    setNewContact(emptyContact);
    setNewContactErrors(emptyErrors);
    setShowAddForm(false);
  };

  const handleNewContactChange = (field: "email" | "phone", value: string) => {
    setNewContact({ ...newContact, [field]: value });
    if (newContactErrors[field]) {
      setNewContactErrors({
        ...newContactErrors,
        [field]: field === "email" ? isValidEmail(value) : isValidPhone(value),
      });
    }
  };

  const handleEditDataChange = (field: "email" | "phone", value: string) => {
    setEditData({ ...editData, [field]: value });
    if (editErrors[field]) {
      setEditErrors({
        ...editErrors,
        [field]: field === "email" ? isValidEmail(value) : isValidPhone(value),
      });
    }
  };

  const contactToDelete = deleteIndex !== null ? contacts[deleteIndex] : null;

  return (
    <Box>
      {contacts.length > 0 && (
        <ArrayField source="contacts">
          <Datagrid bulkActionButtons={false} sx={{ "& .RaDatagrid-headerCell": { fontWeight: 600 } }} rowClick={false}>
            <FunctionField
              source="contact_name"
              label="Name"
              render={(contact: ClientContact) => {
                const index = getContactIndex(contact);
                if (editingIndex === index) {
                  return (
                    <MuiTextField
                      size="small"
                      value={editData.contact_name}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          contact_name: e.target.value,
                        })
                      }
                    />
                  );
                }
                return contact.contact_name;
              }}
            />
            <FunctionField
              source="email"
              label="Email"
              render={(contact: ClientContact) => {
                const index = getContactIndex(contact);
                if (editingIndex === index) {
                  return (
                    <MuiTextField
                      size="small"
                      type="email"
                      value={editData.email}
                      onChange={(e) => handleEditDataChange("email", e.target.value)}
                      onBlur={() =>
                        setEditErrors({
                          ...editErrors,
                          email: isValidEmail(editData.email),
                        })
                      }
                      error={!!editErrors.email}
                      helperText={editErrors.email}
                    />
                  );
                }
                return <EmailField source="email" />;
              }}
            />
            <FunctionField
              source="phone"
              label="Phone"
              render={(contact: ClientContact) => {
                const index = getContactIndex(contact);
                if (editingIndex === index) {
                  return (
                    <MuiTextField
                      size="small"
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => handleEditDataChange("phone", e.target.value)}
                      onBlur={() =>
                        setEditErrors({
                          ...editErrors,
                          phone: isValidPhone(editData.phone),
                        })
                      }
                      error={!!editErrors.phone}
                      helperText={editErrors.phone}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="body2" color="text.secondary">
                                +1
                              </Typography>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  );
                }
                return <PhoneField source="phone" emptyText="—" />;
              }}
            />
            <FunctionField
              source="job_title"
              label="Title"
              render={(contact: ClientContact) => {
                const index = getContactIndex(contact);
                if (editingIndex === index) {
                  return (
                    <MuiTextField
                      size="small"
                      value={editData.job_title}
                      onChange={(e) => setEditData({ ...editData, job_title: e.target.value })}
                    />
                  );
                }
                return contact.job_title || "—";
              }}
            />
            <FunctionField
              label="Status"
              render={(contact: ClientContact) => (
                <Stack direction="row" spacing={0.5}>
                  {contact.is_primary && <Chip label="Primary" size="small" color="primary" />}
                  {contact.is_verified && <Chip label="Verified" size="small" color="success" />}
                </Stack>
              )}
            />
            {/* Only show Actions column if user can edit */}
            {canEdit && (
              <FunctionField
                label="Actions"
                render={(contact: ClientContact) => {
                  const index = getContactIndex(contact);

                  if (editingIndex === index) {
                    return (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Save">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateContact(index)}
                              disabled={isLoading || hasErrors(editErrors)}
                              color="success"
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEditing} disabled={isLoading}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    );
                  }

                  return (
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => startEditing(contact, index)} disabled={isLoading}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={contact.is_primary ? "Primary contact" : "Set as primary"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleSetPrimary(index)}
                            disabled={isLoading || contact.is_primary}
                            color={contact.is_primary ? "primary" : "default"}
                          >
                            {contact.is_primary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {!contact.is_verified && (
                        <Tooltip title="Verify contact">
                          <IconButton size="small" onClick={() => handleVerifyContact(index)} disabled={isLoading}>
                            <VerifyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(index)}
                          disabled={isLoading}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  );
                }}
              />
            )}
          </Datagrid>
        </ArrayField>
      )}

      {/* Only show add form if user can edit */}
      {canEdit && (
        <Collapse in={showAddForm}>
          <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 1 }} alignItems="flex-start">
            <MuiTextField
              label="Name"
              size="small"
              value={newContact.contact_name}
              onChange={(e) => setNewContact({ ...newContact, contact_name: e.target.value })}
            />
            <MuiTextField
              label="Email"
              size="small"
              type="email"
              value={newContact.email}
              onChange={(e) => handleNewContactChange("email", e.target.value)}
              onBlur={() =>
                setNewContactErrors({
                  ...newContactErrors,
                  email: isValidEmail(newContact.email),
                })
              }
              error={!!newContactErrors.email}
              helperText={newContactErrors.email}
            />
            <MuiTextField
              label="Phone"
              size="small"
              type="tel"
              value={newContact.phone}
              onChange={(e) => handleNewContactChange("phone", e.target.value)}
              onBlur={() =>
                setNewContactErrors({
                  ...newContactErrors,
                  phone: isValidPhone(newContact.phone),
                })
              }
              error={!!newContactErrors.phone}
              helperText={newContactErrors.phone}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="text.secondary">
                        +1
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <MuiTextField
              label="Job Title"
              size="small"
              value={newContact.job_title}
              onChange={(e) => setNewContact({ ...newContact, job_title: e.target.value })}
            />
            <Button
              variant="contained"
              onClick={handleAddContact}
              disabled={isLoading || !newContact.contact_name || hasErrors(newContactErrors)}
              size="small"
            >
              Save
            </Button>
            <Button onClick={handleCancel} size="small">
              Cancel
            </Button>
          </Stack>
        </Collapse>
      )}

      {/* Only show Add button if user can edit */}
      {!showAddForm && canEdit && (
        <Box sx={{ mt: contacts.length > 0 ? 2 : 0 }}>
          <Button startIcon={<AddIcon />} size="small" onClick={() => setShowAddForm(true)}>
            Add Contact
          </Button>
        </Box>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Contact"
        message={
          contactToDelete
            ? `Are you sure you want to delete ${contactToDelete.contact_name}?`
            : "Are you sure you want to delete this contact?"
        }
        confirmLabel={ConfirmLabelType.Delete}
        confirmColor={ColorType.Error}
        onConfirm={handleDeleteContact}
        onCancel={closeDeleteDialog}
      />
    </Box>
  );
};
